import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Smartphone, Apple, Monitor, Download } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/admin-shell";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/pwa")({
  component: Page,
});

type Row = {
  id: string;
  platform: string;
  browser: string | null;
  installed_at: string;
  last_seen_at: string;
};

const LABELS: Record<string, string> = {
  android: "Android",
  ios: "iPhone / iPad (iOS)",
  windows: "Windows kompüter",
  macos: "Mac kompüter",
  linux: "Linux kompüter",
  other: "Digər",
};

function iconFor(p: string) {
  if (p === "android") return Smartphone;
  if (p === "ios") return Apple;
  return Monitor;
}

function fmt(d: string) {
  return new Date(d).toLocaleString("az-AZ", { dateStyle: "short", timeStyle: "short" });
}

function Page() {
  const { data, isLoading } = useQuery({
    queryKey: ["pwa-installs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pwa_installs")
        .select("id, platform, browser, installed_at, last_seen_at")
        .order("installed_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const rows = data ?? [];
  const total = rows.length;
  const byPlatform = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.platform] = (acc[r.platform] ?? 0) + 1;
    return acc;
  }, {});
  const mobile = (byPlatform["android"] ?? 0) + (byPlatform["ios"] ?? 0);
  const desktop = total - mobile - (byPlatform["other"] ?? 0);
  const last7 = rows.filter(
    (r) => Date.now() - new Date(r.installed_at).getTime() < 7 * 864e5,
  ).length;

  return (
    <div>
      <AdminPageHeader
        title="PWA quraşdırmaları"
        description="Tətbiqi neçə nəfər telefonuna və ya kompüterinə quraşdırıb"
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: "Ümumi quraşdırma", value: total, icon: Download },
          { label: "Mobil (Android + iOS)", value: mobile, icon: Smartphone },
          { label: "Kompüter", value: desktop, icon: Monitor },
          { label: "Son 7 gün", value: last7, icon: Download },
        ].map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card p-4 shadow-card">
            <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
              <c.icon className="size-4" />
              {c.label}
            </div>
            <p className="mt-2 text-2xl font-extrabold">{isLoading ? "…" : c.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-card p-4 shadow-card">
        <p className="mb-3 text-sm font-bold">Platformalar üzrə</p>
        <div className="grid gap-2">
          {Object.keys(LABELS).map((key) => {
            const count = byPlatform[key] ?? 0;
            const Icon = iconFor(key);
            const pct = total ? Math.round((count / total) * 100) : 0;
            return (
              <div key={key} className="flex items-center gap-3">
                <Icon className="size-4 shrink-0 text-muted-foreground" />
                <span className="w-44 shrink-0 text-xs font-semibold">{LABELS[key]}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-16 text-right text-xs font-bold">
                  {count} ({pct}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card shadow-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="p-3">Platforma</th>
              <th className="p-3">Brauzer</th>
              <th className="p-3">Quraşdırılıb</th>
              <th className="p-3">Son giriş</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="p-4 text-muted-foreground" colSpan={4}>
                  Yüklənir…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="p-4 text-muted-foreground" colSpan={4}>
                  Hələ quraşdırma qeyd olunmayıb.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60 last:border-0">
                  <td className="p-3 font-semibold">{LABELS[r.platform] ?? r.platform}</td>
                  <td className="p-3">{r.browser ?? "—"}</td>
                  <td className="p-3 text-muted-foreground">{fmt(r.installed_at)}</td>
                  <td className="p-3 text-muted-foreground">{fmt(r.last_seen_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
